import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/**
 * Formats a timestamp/date as "Mon D, YYYY" (e.g., "Apr 15, 2025")
 * @param dateString - Date string to format
 * @returns Formatted date string, or empty string if input is invalid
 */
export function formatDateShort(dateString: string | null | undefined): string {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Returns a relative time string (e.g., "2 days ago", "in 3 days")
 * @param dateString - Date string to format
 * @returns Relative time string, or empty string if input is invalid
 */
export function formatFromNow(dateString: string | null | undefined): string {
  if (!dateString) {
    return "";
  }
  const date = dayjs(dateString);
  if (!date.isValid()) {
    return "";
  }
  return date.fromNow();
}

/**
 * Returns a day-based relative string for date-only values (no time component)
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns "Today", "Tomorrow", "Yesterday", "In X days", or "X days ago"
 */
export function formatRelativeDay(dateString: string | null | undefined): string {
  if (!dateString) {
    return "";
  }

  // Parse the date string as local date (not UTC)
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return "";
  }

  const [, year, month, day] = match.map(Number);
  const targetDate = new Date(year, month - 1, day);

  // Get today's date at midnight local time
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate difference in days
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 1) return `In ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
}

/**
 * Formats a timestamp/date as MM/DD/YYYY
 * @param timestamp - Date string, timestamp, or Date object to format
 * @returns Formatted date string in MM/DD/YYYY format, or empty string if input is invalid
 */
export function formatDate(timestamp: string | Date | null | undefined): string {
  if (!timestamp) {
    return '';
  }
  
  // If it's a string in YYYY-MM-DD format, parse it directly to avoid timezone issues
  if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
    const [year, month, day] = timestamp.split('-').map(Number);
    
    // Validate the parsed values
    if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }
    
    return `${month.toString().padStart(2, "0")}/${day.toString().padStart(2, "0")}/${year}`;
  }
  
  // For ISO timestamps (with time component), extract just the date part to avoid timezone issues
  if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(timestamp)) {
    const dateOnly = timestamp.split('T')[0]; // Extract YYYY-MM-DD part
    const [year, month, day] = dateOnly.split('-').map(Number);
    
    // Validate the parsed values
    if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }
    
    return `${month.toString().padStart(2, "0")}/${day.toString().padStart(2, "0")}/${year}`;
  }
  
  // For Date objects or other string formats, use Date object
  const date = new Date(timestamp);
  
  // Check for invalid date
  if (isNaN(date.getTime())) {
    return '';
  }
  
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Formats a timestamp/date as MM/DD/YYYY, HH:MM AM/PM
 * @param timestamp - Date string or Date object to format
 * @returns Formatted date and time string (e.g., "08/05/2025, 11:34 AM"), or empty string if input is invalid
 */
export function formatDateWithTime(timestamp: string | Date | null | undefined): string {
  if (!timestamp) {
    return '';
  }
  
  const date = new Date(timestamp);
  
  // Check for invalid date
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // Get date components
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  
  // Get time components
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours || 12; // Handle 0 hours (midnight)
  const formattedHours = hours.toString().padStart(2, "0");
  
  return `${month}/${day}/${year}, ${formattedHours}:${minutes} ${ampm}`;
}